import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import type { PrimitiveKeyInterface } from '@zaoblako/shared/common/interfaces/primitive-key.interface';
import type { SerdeContextInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context.interface';

/**
 * Common singature for DTO with selection settings. This DTO can be composed with another query parameters.
 *
 * @template T Type of the object with selectable fields
 */
export interface SelectFieldsInterface<T> {
  /**
   * Fields to select.
   * Top-level keys of `T` always; with `maxDepth` in {@link createSelectFieldsDto} also dotted paths
   * (e.g. `planning.periodicity`) along the Serde `nested` metadata tree.
   */
  select?: (keyof T)[];
}

/** {@link createSelectFieldsDto} function additional options */
export interface CreateSelectFieldsDtoOptions<T = unknown> {
  /** Custom label value or factory */
  label?: string | ((context: SerdeContextInterface) => string);
  /** Field hint for API docs / metadata */
  hint?: string;
  /** Max nested properties depth for dotted `select` paths (e.g. `planning.periodicity`) */
  maxDepth?: number;
  /** Default selected fields when `select` is omitted */
  default?: (keyof T)[];
  /**
   * Additional `select` key patterns (e.g. pivot month columns `YYYYMM`).
   * Static keys still come from the target DTO metadata.
   */
  extraKeyPatterns?: RegExp[];
}

/**
 * Create selection settings DTO schema. This DTO can be composed with another query parameters.
 *
 * @template T Type of the object with selectable fields
 *
 * @param key New DTO unique key
 * @param target DTO schema with selectable fields (key or schema instance)
 * @param [options] Custom label or options
 *
 * @returns DTO schema ready to use
 */
export function createSelectFieldsDto<T>(
  key: PrimitiveKeyInterface,
  target: PrimitiveKeyInterface | SerdeDtoClass<T>,
  options?: string | ((context: SerdeContextInterface) => string) | CreateSelectFieldsDtoOptions<T>,
): SerdeDtoClass<SelectFieldsInterface<T>> {
  const resolvedOptions: CreateSelectFieldsDtoOptions<T> = typeof options === 'string' || typeof options === 'function'
    ? { label: options }
    : options ?? {};

  return SerdeDtoClass.create<SelectFieldsInterface<T>>(key, {
    select: {
      optional: true,
      label: resolvedOptions.label || 'Выбрать поля',
      ...(resolvedOptions.hint ? { hint: resolvedOptions.hint } : {}),
      ...(resolvedOptions.default?.length ? { default: [...resolvedOptions.default] } : {}),
      serializer: SerdeDtoClass.getSelectablePropertiesValidator(
        target,
        resolvedOptions.maxDepth,
        resolvedOptions.extraKeyPatterns,
      ),
    },
  });
}
